import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TotalTimeSaved from './TotalTimeSaved';
import LanguageSelector from './LanguageSelector';
import { ThemeToggle } from './ThemeToggle';
import { TrophiesDropdown } from './TrophiesDropdown';
import { useAuth } from '../hooks/useAuth';
import { KroktimeIcon } from './Logo';

interface HeaderProps {
  onAuthModalOpen?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onAuthModalOpen }) => {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Essayons d'utiliser le hook de manière sûre
  let user = null;
  let isAuthenticated = false;
  let logout = () => {};
  let isLoading = false;

  try {
    const authData = useAuth();
    user = authData.user;
    isAuthenticated = authData.isAuthenticated;
    logout = authData.logout;
    isLoading = authData.isLoading;
  } catch (error) {
    console.error('Hook useAuth non disponible, mode déconnecté par défaut');
  }

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 dark:bg-gray-800 dark:border-gray-700">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo et titre */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center flex-shrink-0 text-blue-600">
              <KroktimeIcon size={50} />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">
                {t('header.title')}
              </span>
              <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                {t('header.subtitle')}
              </span>
            </div>
          </div>

          {/* Menu desktop */}
          <div className="hidden lg:flex items-center space-x-2 xl:space-x-4">
            {/* Navigation d'authentification normale */}
            {isLoading ? (
              /* État de chargement */
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-gray-500">Chargement...</span>
              </div>
            ) : isAuthenticated && user ? (
              <div className="flex items-center space-x-3 xl:space-x-4">
                {/* Informations utilisateur */}
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">
                      {user?.name
                        ? user.name[0].toUpperCase()
                        : user?.email?.[0]?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className="hidden xl:block">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {user?.name ||
                        user?.email?.split('@')[0] ||
                        'Utilisateur'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      ⏱️ {Math.round(user?.totalMinutesSaved || 0)}min
                      économisées
                    </div>
                  </div>
                </div>

                {/* Bouton de déconnexion */}
                <button
                  onClick={logout}
                  className="text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 px-3 py-2 rounded-lg transition-colors duration-200"
                  title="Se déconnecter"
                >
                  🚪 <span className="hidden xl:inline">Déconnexion</span>
                </button>
              </div>
            ) : null}

            {/* Trophées (seulement si connecté) */}
            <TrophiesDropdown />

            {/* Sélecteur de langue */}
            <LanguageSelector />

            {/* Toggle de thème */}
            <ThemeToggle showLabel={false} />

            {/* Temps total gagné */}
            <TotalTimeSaved />

            <div className="flex items-center space-x-2 p-2">
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>

              <button
                onClick={() => {
                  onAuthModalOpen && onAuthModalOpen();
                }}
                className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium px-3 py-2 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors duration-200"
              >
                🔐 <span className="hidden xl:inline">Connexion</span>
              </button>
            </div>
          </div>

          {/* Menu mobile - bouton hamburger */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
              aria-label="Menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Menu mobile déroulant */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="space-y-4">
              {/* État d'authentification mobile */}
              {isLoading ? (
                <div className="flex items-center space-x-2 py-2">
                  <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-gray-500">Chargement...</span>
                </div>
              ) : isAuthenticated && user ? (
                <div className="space-y-3">
                  {/* Informations utilisateur mobile */}
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-bold">
                        {user?.name
                          ? user.name[0].toUpperCase()
                          : user?.email?.[0]?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {user?.name ||
                          user?.email?.split('@')[0] ||
                          'Utilisateur'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        ⏱️ {Math.round(user?.totalMinutesSaved || 0)}min
                        économisées
                      </div>
                    </div>
                  </div>

                  {/* Bouton de déconnexion mobile */}
                  <button
                    onClick={logout}
                    className="w-full text-left text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 px-3 py-2 rounded-lg transition-colors duration-200"
                  >
                    🚪 Se déconnecter
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    onAuthModalOpen && onAuthModalOpen();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium px-3 py-2 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  🔐 Se connecter
                </button>
              )}

              {/* Trophées mobile */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                <TrophiesDropdown />
              </div>

              {/* Contrôles mobiles */}
              <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-3">
                <LanguageSelector />
                <ThemeToggle showLabel={true} />
              </div>

              {/* Temps total gagné mobile */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                <TotalTimeSaved />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
